/**
 * Lightweight canvas bar charts for PDF embedding.
 * Covers the stacked / horizontal / single-series cases previously
 * rendered via Chart.js in pdfUtils.
 */

function toNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function colorAt(backgroundColor, index) {
  if (Array.isArray(backgroundColor)) {
    return backgroundColor[index] || backgroundColor[0] || '#888'
  }
  return backgroundColor || '#888'
}

function computeMax(labels, datasets, { stacked, horizontal  }) {
  let max = 0
  for (let i = 0; i < labels.length; i += 1) {
    if (stacked && !horizontal) {
      let sum = 0
      for (const ds of datasets) sum += Math.max(0, toNumber(ds.data[i]))
      max = Math.max(max, sum)
    } else {
      for (const ds of datasets) {
        max = Math.max(max, Math.abs(toNumber(ds.data[i])))
      }
    }
  }
  return max > 0 ? max : 1
}

function drawLegend(ctx, datasets, area) {
  const box = 10
  const gap = 8
  const lineH = 16
  ctx.font = 'bold 12px Helvetica, Arial, sans-serif'
  ctx.textBaseline = 'middle'

  let x = area.x
  let y = area.y + lineH / 2
  const maxX = area.x + area.w

  for (const ds of datasets) {
    if (!ds.label) continue
    const color = colorAt(ds.backgroundColor, 0)
    const textW = ctx.measureText(ds.label).width
    const needed = box + 6 + textW + gap * 2
    if (x + needed > maxX) {
      x = area.x
      y += lineH + 4
    }
    ctx.fillStyle = color
    ctx.fillRect(x, y - box / 2, box, box)
    ctx.fillStyle = '#333'
    ctx.fillText(ds.label, x + box + 6, y)
    x += needed
  }

  return y + lineH / 2 + 4
}

/**
 * @returns {string} PNG data URL
 */
export function renderBarChartToDataUrl({
  width,
  height,
  labels = [],
  datasets = [],
  stacked = false,
  horizontal = false,
  showLegend = true,
  formatTick = (value) => String(value),
  stepSize,
}) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)

  const pad = { top: 16, right: 20, bottom: 28, left: horizontal ? 140 : 56 }
  let legendBottom = pad.top

  if (showLegend && datasets.some((d) => d.label)) {
    legendBottom = drawLegend(ctx, datasets, {
      x: pad.left,
      y: pad.top,
      w: width - pad.left - pad.right,
    })
    legendBottom += 8
  }

  const plot = {
    x: pad.left,
    y: legendBottom,
    w: width - pad.left - pad.right,
    h: height - legendBottom - pad.bottom,
  }

  const maxVal = computeMax(labels, datasets, { stacked, horizontal  })
  const niceMax = stepSize
    ? Math.ceil(maxVal / stepSize) * stepSize
    : maxVal * 1.05

  // Axes
  ctx.strokeStyle = '#ccc'
  ctx.lineWidth = 1
  ctx.beginPath()
  if (horizontal) {
    ctx.moveTo(plot.x, plot.y)
    ctx.lineTo(plot.x, plot.y + plot.h)
    ctx.lineTo(plot.x + plot.w, plot.y + plot.h)
  } else {
    ctx.moveTo(plot.x, plot.y)
    ctx.lineTo(plot.x, plot.y + plot.h)
    ctx.lineTo(plot.x + plot.w, plot.y + plot.h)
  }
  ctx.stroke()

  // Grid / ticks
  ctx.fillStyle = '#666'
  ctx.font = '11px Helvetica, Arial, sans-serif'
  const tickStep = stepSize || niceMax / 8
  if (horizontal) {
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    for (let v = 0, i = 0; v <= niceMax + 1e-6; v += tickStep, i += 1) {
      const x = plot.x + (v / niceMax) * plot.w
      ctx.strokeStyle = '#eee'
      ctx.beginPath()
      ctx.moveTo(x, plot.y)
      ctx.lineTo(x, plot.y + plot.h)
      ctx.stroke()
      const label = formatTick(v, i)
      if (label) ctx.fillText(String(label), x, plot.y + plot.h + 4)
    }
  } else {
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    for (let v = 0, i = 0; v <= niceMax + 1e-6; v += tickStep, i += 1) {
      const y = plot.y + plot.h - (v / niceMax) * plot.h
      ctx.strokeStyle = '#eee'
      ctx.beginPath()
      ctx.moveTo(plot.x, y)
      ctx.lineTo(plot.x + plot.w, y)
      ctx.stroke()
      const label = formatTick(v, i)
      if (label) ctx.fillText(String(label), plot.x - 6, y)
    }
  }

  const n = labels.length || 1
  if (horizontal) {
    const band = plot.h / n
    const barH = Math.max(4, band * 0.55)
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#333'
    ctx.font = '11px Helvetica, Arial, sans-serif'

    for (let i = 0; i < n; i += 1) {
      const cy = plot.y + band * i + band / 2
      const label = String(labels[i] ?? '')
      const truncated =
        label.length > 28 ? `${label.slice(0, 26)}…` : label
      ctx.fillText(truncated, plot.x - 8, cy)

      // single dataset typical for horizontal
      const ds = datasets[0] || { data: [], backgroundColor: '#888' }
      const val = Math.max(0, toNumber(ds.data[i]))
      const barW = (val / niceMax) * plot.w
      ctx.fillStyle = colorAt(ds.backgroundColor, i)
      ctx.fillRect(plot.x, cy - barH / 2, barW, barH)
    }
  } else {
    const band = plot.w / n
    const groupPad = band * 0.18
    const barArea = band - groupPad * 2

    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.fillStyle = '#333'
    ctx.font = '11px Helvetica, Arial, sans-serif'

    for (let i = 0; i < n; i += 1) {
      const x0 = plot.x + band * i + groupPad
      ctx.fillStyle = '#333'
      ctx.fillText(String(labels[i] ?? ''), x0 + barArea / 2, plot.y + plot.h + 4)

      if (stacked) {
        let yCursor = plot.y + plot.h
        for (const ds of datasets) {
          const val = Math.max(0, toNumber(ds.data[i]))
          const h = (val / niceMax) * plot.h
          yCursor -= h
          ctx.fillStyle = colorAt(ds.backgroundColor, i)
          ctx.fillRect(x0, yCursor, barArea, h)
        }
      } else {
        const barW = barArea / Math.max(1, datasets.length)
        datasets.forEach((ds, di) => {
          const val = Math.max(0, toNumber(ds.data[i]))
          const h = (val / niceMax) * plot.h
          const x = x0 + di * barW
          ctx.fillStyle = colorAt(ds.backgroundColor, i)
          ctx.fillRect(x, plot.y + plot.h - h, barW * 0.9, h)
        })
      }
    }
  }

  return canvas.toDataURL('image/png')
}

export default renderBarChartToDataUrl
